import { createContext, useContext } from 'react'
import { ApiClient, initApiClient } from '@frontend/lib/request'

import { FormLocation } from '@shared/const'

import {
  Badge,
  BadgeCreationPayload,
  BadgeFilterParams,
  BadgeUpdatePayload,
  CompanyForm,
  CompanyFormCreationPayload,
  CompanyFormUpdatePayload,
  Driver,
  DriverCreationPayload,
  DriverFilterParams,
  DriverUpdatePayload,
  FormFilterParams,
  ListCompanyAuditEventTypesQuery,
} from '@shared-types/company'
import { AnyCompanyAuditEvent } from '@shared-types/company/audit-event'
import {
  Company,
  CompanyCreationPayload,
  CompanyListFilterParam,
  CompanyListRamp,
  CompanyListRampsFilterParams,
  CompanyLogoUpdatePayload,
  CompanyUpdatePayload,
  Ramp,
  RampCreationPayload,
} from '@shared-types/domain'
import {
  CompanySettingsUpdatePayload,
  DBCompanySetting,
  DBCompanySettingUpdatePayload,
} from '@shared-types/settings'
import { CompanySetting } from '@shared-types/settings/index'
import { Collection, Identifier, Nullable } from '@shared-types/util'

export interface CompaniesClient {
  list: (params: CompanyListFilterParam) => Promise<Collection<Company>>
  get: (companyId: Identifier) => Promise<Company>
  getSettings: (companyId: Identifier) => Promise<Collection<CompanySetting>>
  create: (payload: CompanyCreationPayload) => Promise<Company>
  update: (companyId: Identifier, payload: CompanyUpdatePayload) => Promise<Company>
  updateLogo: (companyId: Identifier, payload: CompanyLogoUpdatePayload) => Promise<Company>
  listCompanyAuditEvents: (
    companyId: Identifier,
    filters: ListCompanyAuditEventTypesQuery
  ) => Promise<Collection<AnyCompanyAuditEvent>>
  listDrivers: (companyId: Identifier, params: DriverFilterParams) => Promise<Collection<Driver>>
  createDriver: (companyId: Identifier, payload: DriverCreationPayload) => Promise<Driver>
  updateDriver: (
    companyId: Identifier,
    driverId: Identifier,
    payload: DriverUpdatePayload
  ) => Promise<Driver>
  deleteDriver: (companyId: Identifier, driverId: Identifier) => Promise<void>
  listAllBadges: (companyId: Identifier, facilityId?: Identifier) => Promise<Badge[]>
  listBadges: (companyId: Identifier, params: BadgeFilterParams) => Promise<Collection<Badge>>
  listForms: (companyId: Identifier, params: FormFilterParams) => Promise<Collection<CompanyForm>>
  createForm: (companyId: Identifier, payload: CompanyFormCreationPayload) => Promise<CompanyForm>
  getForm: (companyId: Identifier, formId: Identifier) => Promise<CompanyForm>
  getFormByLocation: (
    companyId: Identifier,
    formLocation: FormLocation
  ) => Promise<CompanyForm | null>
  updateForm: (
    companyId: Identifier,
    formId: Identifier,
    payload: CompanyFormUpdatePayload
  ) => Promise<CompanyForm>
  listRamps: (
    companyId: Identifier,
    params?: CompanyListRampsFilterParams
  ) => Promise<CompanyListRamp[]>
  createBadge: (companyId: Identifier, payload: BadgeCreationPayload) => Promise<Badge>
  updateBadge: (
    companyId: Identifier,
    badgeId: Identifier,
    payload: BadgeUpdatePayload
  ) => Promise<Badge>
  createRamp: (
    companyId: Identifier,
    facilityId: Identifier,
    payload: RampCreationPayload
  ) => Promise<Ramp>
  internalUpdateCompanySetting: (
    companyId: Identifier,
    settingName: string,
    payload: DBCompanySettingUpdatePayload
  ) => Promise<DBCompanySetting>
  updateCompanySettings: (
    companyId: Identifier,
    payload: CompanySettingsUpdatePayload
  ) => Promise<DBCompanySetting[]>
  getLogoURL: (companyId: Identifier) => Promise<{ url: Nullable<string> }>
}

class DefaultCompaniesClient implements CompaniesClient {
  client: ApiClient
  constructor() {
    this.client = initApiClient({})
  }

  async list(params: CompanyListFilterParam): Promise<Collection<Company>> {
    return this.client.get(`/companies`, { params }).then((res) => res.data)
  }

  async get(companyId: Identifier): Promise<Company> {
    return this.client.get(`/companies/${companyId}`).then((res) => res.data)
  }

  async getSettings(companyId: Identifier): Promise<Collection<CompanySetting>> {
    return this.client.get(`/companies/${companyId}/settings`).then((res) => res.data)
  }

  async create(payload: CompanyCreationPayload): Promise<Company> {
    return this.client.post(`/companies`, payload).then((res) => res.data)
  }

  async update(companyId: Identifier, payload: CompanyUpdatePayload): Promise<Company> {
    return this.client.patch(`/companies/${companyId}`, payload).then((res) => res.data)
  }

  async updateLogo(companyId: Identifier, payload: CompanyLogoUpdatePayload): Promise<Company> {
    return this.client.patch(`/companies/${companyId}/logo_url`, payload).then((res) => res.data)
  }

  async listCompanyAuditEvents(companyId: Identifier, filters: ListCompanyAuditEventTypesQuery) {
    const params = {
      ...filters,
      types: filters.types && filters.types.length >= 1 ? filters.types.toString() : undefined,
      sort_by:
        filters.sort_by && filters.sort_by.length >= 1 ? filters.sort_by.toString() : undefined,
    }

    return this.client
      .get(`/companies/${companyId}/audit_events`, { params })
      .then((res) => res.data)
  }

  async listDrivers(companyId: Identifier, params: DriverFilterParams) {
    return this.client.get(`/companies/${companyId}/drivers`, { params }).then((res) => res.data)
  }

  async createDriver(companyId: Identifier, payload: DriverCreationPayload) {
    return this.client.post(`/companies/${companyId}/drivers`, payload).then((res) => res.data)
  }

  async updateDriver(companyId: Identifier, driverId: Identifier, payload: DriverUpdatePayload) {
    return this.client
      .patch(`/companies/${companyId}/drivers/${driverId}`, payload)
      .then((res) => res.data)
  }

  async deleteDriver(companyId: Identifier, driverId: Identifier) {
    return this.client.delete(`/companies/${companyId}/drivers/${driverId}`).then((res) => res.data)
  }

  async listAllBadges(companyId: Identifier, facilityId?: Identifier) {
    return this.client
      .get(`/companies/${companyId}/badges/all`, { params: { facility_id: facilityId } })
      .then((res) => res.data)
  }

  async listBadges(companyId: Identifier, params: BadgeFilterParams) {
    return this.client.get(`/companies/${companyId}/badges`, { params }).then((res) => res.data)
  }

  async listForms(companyId: Identifier, params: FormFilterParams) {
    return this.client.get(`/companies/${companyId}/forms`, { params }).then((res) => res.data)
  }

  async createForm(companyId: Identifier, payload: CompanyFormCreationPayload) {
    return this.client.post(`/companies/${companyId}/forms`, payload).then((res) => res.data)
  }

  async getForm(companyId: Identifier, formId: Identifier) {
    return this.client.get(`/companies/${companyId}/forms/${formId}`).then((res) => res.data)
  }

  async getFormByLocation(companyId: Identifier, formLocation: FormLocation) {
    return this.client
      .get(`/companies/${companyId}/forms/location/${formLocation}`)
      .then((res) => res.data)
  }

  async updateForm(
    companyId: Identifier,
    formId: Identifier,
    payload: CompanyFormUpdatePayload
  ): Promise<CompanyForm> {
    return this.client
      .patch(`/companies/${companyId}/forms/${formId}`, payload)
      .then((res) => res.data)
  }

  async listRamps(companyId: Identifier, params?: CompanyListRampsFilterParams) {
    return this.client.get(`/companies/${companyId}/ramps`, { params }).then((res) => res.data)
  }

  async createBadge(companyId: Identifier, payload: BadgeCreationPayload): Promise<Badge> {
    return this.client.post(`/companies/${companyId}/badges`, payload).then((res) => res.data)
  }

  async updateBadge(
    companyId: Identifier,
    badgeId: Identifier,
    payload: BadgeUpdatePayload
  ): Promise<Badge> {
    return this.client
      .patch(`/companies/${companyId}/badges/${badgeId}`, payload)
      .then((res) => res.data)
  }

  async createRamp(
    companyId: Identifier,
    facilityId: Identifier,
    payload: RampCreationPayload
  ): Promise<Ramp> {
    return this.client
      .post(`/companies/${companyId}/facilities/${facilityId}/ramps`, payload)
      .then((res) => res.data)
  }

  async internalUpdateCompanySetting(
    companyId: Identifier,
    settingName: string,
    payload: DBCompanySettingUpdatePayload
  ): Promise<DBCompanySetting> {
    return this.client
      .patch(`/internal/admin/companies/${companyId}/settings/${settingName}`, payload)
      .then((res) => res.data)
  }

  async updateCompanySettings(
    companyId: Identifier,
    payload: CompanySettingsUpdatePayload
  ): Promise<DBCompanySetting[]> {
    return this.client.patch(`/companies/${companyId}/settings`, payload).then((res) => res.data)
  }

  async getLogoURL(companyId: Identifier): Promise<{ url: Nullable<string> }> {
    return this.client.get(`/companies/${companyId}/logo_url`).then((res) => res.data)
  }
}

const CompaniesClientContext = createContext<CompaniesClient>(new DefaultCompaniesClient())

export function useCompaniesClient() {
  return useContext(CompaniesClientContext)
}
